// A Save made in this browser (spec docs/specs/editor/fast-save.md): the saved version's file made
// here with the server's preset, as Download makes a file (`engine/export.ts`), posted to the
// render's output with the policy the server signs, and said sent, so the render takes it in at
// once. Whatever this tab cannot finish goes to the server, which renders the same saved version:
// a make or a post that fails, the editor left, the tab closed.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { fetchSignedFile } from "../../api/download";
import { isApiError } from "../../api/errors";
import { sendToBucket } from "../../api/upload";
import { toUserMessage } from "../../platform/messages";
import { removeExportFiles } from "./engine/opfs";
import { SERVER_PRESET } from "./engine/presets";
import type { EngineProject } from "./engine/project";
import { engineSupported } from "./engine/support";
import { browserRenderRoutes, type EditorRender } from "./project";

/**
 * Where a Save made here stands: its frames being made, then its file being posted, each `done`
 * of 1; posted and said sent; or failed, when the server could not take it over either.
 */
export type HereSave = HereProgress | { phase: "failed"; problem: string };

/** A Save made here that runs, or is posted: how far, `done` of 1 within its phase. */
export type HereProgress = { phase: "making" | "sending" | "sent"; done: number };

/** How often, at most, the row's percent moves: the make runs at its own pace. */
const PROGRESS_STEP_MS = 100;

/**
 * Whether this browser makes the server's file of `project`: WebGL, WebCodecs, and an H.264 and
 * AAC encoder at its frame (Chrome on Linux has no AAC). The engine loads only when it might.
 */
export async function canMakeHere(project: EngineProject): Promise<boolean> {
  if (typeof VideoEncoder === "undefined" || typeof AudioEncoder === "undefined" || !engineSupported()) {
    return false;
  }
  try {
    const engine = await import("./engine/export");
    return (await engine.refusalOf(project, SERVER_PRESET)) === null;
  } catch {
    // The engine could not load or answer (a chunk gone after a deploy): the server makes it.
    return false;
  }
}

/** A render's one hand-over key: a failure, a leave and a closing tab hand it over once. */
function handOverKey(jobId: string): string {
  return `${jobId}:server`;
}

/**
 * The Saves made in this browser, by their render's job. `make` runs one; `onHandedOver` hears
 * that the server took one over, with the render that makes its file now, and `onFailed` that
 * the server could not take one over either.
 */
export function useBrowserSaves(options: {
  teamId: string;
  brandId: string;
  projectId: string;
  onHandedOver: (from: string, to: EditorRender) => void;
  onFailed: (jobId: string) => void;
}) {
  const { teamId, brandId, projectId, onHandedOver, onFailed } = options;
  const [saves, setSaves] = useState<Readonly<Record<string, HereSave>>>({});
  // The Saves this tab still makes or posts: the ones a leave hands to the server.
  const running = useRef(new Map<string, AbortController>());
  // Whether the editor is still open: a render whose start is answered after it was left is
  // handed to the server at once, never made by a page no one watches.
  const open = useRef(true);
  const routes = useMemo(() => browserRenderRoutes(teamId, brandId, projectId), [teamId, brandId, projectId]);
  const latest = useRef({ routes, onHandedOver, onFailed });
  useEffect(() => {
    latest.current = { routes, onHandedOver, onFailed };
  });
  const patch = useCallback((jobId: string, save: HereSave | null) => {
    setSaves((current) => {
      const next = { ...current };
      if (save === null) delete next[jobId];
      else next[jobId] = save;
      return next;
    });
  }, []);

  const handOver = useCallback(
    async (jobId: string) => {
      try {
        // `keepalive` lets it outlive the page, should the tab close before it is answered.
        const render = await latest.current.routes.toServer(jobId, handOverKey(jobId), true);
        patch(jobId, null);
        latest.current.onHandedOver(jobId, render);
      } catch (error) {
        // It got its file meanwhile: the render finishes as it is.
        if (isApiError(error, "JOB_FINALIZING") || isApiError(error, "JOB_TERMINAL")) {
          patch(jobId, { phase: "sent", done: 1 });
          return;
        }
        patch(jobId, { phase: "failed", problem: toUserMessage(error) });
        latest.current.onFailed(jobId);
      }
    },
    [patch],
  );

  // Leaving the editor stops what this tab makes and hands it to the server, and so does closing
  // the tab, whose hand-over is sent to outlive the page.
  useEffect(() => {
    open.current = true;
    const controllers = running.current;
    const closing = () => {
      for (const [jobId, controller] of controllers) {
        controller.abort();
        latest.current.routes.toServer(jobId, handOverKey(jobId), true).catch(() => undefined);
      }
      controllers.clear();
    };
    window.addEventListener("pagehide", closing);
    return () => {
      open.current = false;
      window.removeEventListener("pagehide", closing);
      for (const [jobId, controller] of controllers) {
        controller.abort();
        void handOver(jobId);
      }
      controllers.clear();
    };
  }, [handOver]);

  /** Makes the file of `project`, the saved version, for render `jobId`, and posts it. */
  const make = useCallback(
    async (jobId: string, project: EngineProject) => {
      // The editor was left while the render started: the server makes it.
      if (!open.current) return handOver(jobId);
      const controller = new AbortController();
      running.current.set(jobId, controller);
      const name = `save-${jobId}.mp4`;
      let shown = 0;
      const say = (save: HereProgress) => {
        const now = performance.now();
        if (now - shown < PROGRESS_STEP_MS && save.done < 1) return;
        shown = now;
        patch(jobId, save);
      };
      patch(jobId, { phase: "making", done: 0 });
      try {
        const engine = await import("./engine/export");
        const file = await engine.exportFile(project, SERVER_PRESET, {
          fetchFile: fetchSignedFile,
          signal: controller.signal,
          onProgress: (done, frames) => say({ phase: "making", done: frames === 0 ? 1 : done / frames }),
          // Its own file in the browser's file system (E2 decision 28), posted from there.
          name,
        });
        patch(jobId, { phase: "sending", done: 0 });
        const ticket = await latest.current.routes.file(jobId, file.size, controller.signal);
        await sendToBucket(ticket, file, {
          signal: controller.signal,
          onProgress: (fraction) => say({ phase: "sending", done: fraction }),
        });
      } catch {
        // Left or closed: that path hands it over. Else the server makes it.
        if (!controller.signal.aborted) {
          running.current.delete(jobId);
          await handOver(jobId);
        }
        return;
      } finally {
        void removeExportFiles([name]);
      }
      running.current.delete(jobId);
      patch(jobId, { phase: "sent", done: 1 });
      // A lost word costs only the render's next poll, 10 s on.
      await latest.current.routes.sent(jobId).catch(() => undefined);
    },
    [handOver, patch],
  );

  return { saves, make };
}
